import axios from 'axios'
import { modifySortProperty } from '@utils/transformations'
import defaultRequestParams from '@utils/helpers/default-request-params'
import store from '@state/store'

const axiosRaw = axios.create()

const internalServerErrorHandler = error => {
  const { path, message } = error.data
  let errorMsg = message
  if (path === '/api/1/organization/') {
    if (message.includes('could not execute statement;'))
      errorMsg = 'error__customer_number_already_exists'
  }
  store.commit('notifications/ADD', {
    type: 'error',
    message: errorMsg || 'Some error',
  })
}

const forbiddenErrorHandler = error => {
  store.commit('notifications/ADD', {
    type: 'error',
    message: error.data.error || 'Some error',
  })
}

axios.interceptors.response.use(
  response => response,
  error => {
    return handleError(error)
  }
)

export function handleError(error) {
  switch (error.response.status) {
    case 500:
      if (error.response.data && error.response.data.code !== '8')
        internalServerErrorHandler(error.response)
      break
    case 403:
      forbiddenErrorHandler(error.response)
      break
    default:
      break
  }
  return Promise.reject(error)
}

export function getPermissions(calls) {
  return executeAll(calls, responses => {
    return {
      roles: responses[0],
      regions: responses[1],
      organisations: responses[2],
    }
  })
}

export function getDefaultOrganization(id) {
  return axios
    .get(`/api/1/organization/${id}`)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function getOrganizationChildren(id, requestParams) {
  const params = requestParams
    ? {
        ...requestParams,
        size: requestParams.size,
        page: requestParams.page - 1,
        sort_property: modifySortProperty(requestParams.sort_property),
        sort_direction: requestParams.sort_direction ? 'DESC' : 'ASC',
      }
    : {
        size: 10,
        page: 0,
        sort_property: 'shortName',
        sort_direction: 'ASC',
      }
  return axios
    .get(`/api/1/organization/${id}/child`, {
      params: {
        ...params,
      },
    })
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function getOrganizationChild(id) {
  return axios
    .get(`/api/1/organization/${id}/child`)
    .then(response => {
      const warehouses = response.data.filter(item => item.type === 1)
      if (warehouses.length) return warehouses
      else {
        return axios.get(`/api/1/organization/${id}`).then(response => {
          const result = []
          result.push(response.data)
          return result
        })
      }
    })
    .catch(() => {
      return axios.get(`/api/1/organization/${id}`).then(response => {
        const result = []
        result.push(response.data)
        return result
      })
    })
    .catch(() => {})
}

export function createDocument(data) {
  return axios
    .post('/api/1/document', data)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function deleteDocument(id) {
  return axios
    .delete(`/api/1/document/${id}`)
    .then(response => {
      return response
    })
    .catch(() => {})
}

export function getDocument(id) {
  return axios
    .get(`/api/1/document/${id}`)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function finalizeDocument(id) {
  return axios
    .post(`/api/1/document/${id}/finalization`)
    .then(response => {
      return response
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

export function finalizeDocumentNoErrorHandler(id, isConfirmed) {
  return axiosRaw
    .post(
      `/api/1/document/${id}/finalization` + (isConfirmed ? '?confirmed' : '')
    )
    .then(response => {
      return response
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

export function shipNoErrorHandler(data) {
  return axiosRaw
    .post(`/api/1/devices/ship`, data)
    .then(response => response)
    .catch(error => {
      return Promise.reject(error)
    })
}

export function markDeviceAsReady(serial) {
  return axios
    .post(`/api/1/devices/${serial}/ready`, {})
    .then(response => response)
    .catch(() => {})
}

export function updateDocument(data) {
  return axios
    .put(`/api/1/document/`, data)
    .then(response => {
      return response
    })
    .catch(() => {})
}

export function getSpecifications(payload) {
  return axios
    .get(`/api/1/document/${payload.documentId}/specification`, {
      params: {
        size: payload.params.size,
        page: payload.params.page - 1,
        // sort_property: modifySortProperty(payload.params.sort_property),
        // sort_direction: payload.params.sort_direction ? 'DESC' : 'ASC',
        sort_property: null,
        sort_direction: null,
      },
    })
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function getSerialNumbers(
  orgwarehouseid,
  goodsgroupcode,
  goodsid,
  indraft
) {
  return axios
    .get(`/api/1/warehouse/serials`, {
      params: {
        orgwarehouseid,
        goodsgroupcode,
        goodsid,
        indraft: indraft || false,
      },
    })
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function createSpecification(data) {
  return axios
    .post(`/api/1/documentspecification/`, data)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function deleteSpecification(id) {
  return axios
    .delete(`/api/1/documentspecification/${id}`)
    .then(response => {
      return response
    })
    .catch(() => {})
}

export function updateSpecification(data) {
  return axios
    .put(`/api/1/documentspecification/`, data)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function getDictionary(name, requestParams) {
  const params = requestParams
    ? {
        size: requestParams.size,
        page: requestParams.page - 1,
        sort_property: modifySortProperty(requestParams.sort_property),
        sort_direction: requestParams.sort_direction ? 'DESC' : 'ASC',
      }
    : {
        size: 10,
        page: 0,
        sort_property: 'name',
        sort_direction: 'ASC',
      }
  return axios
    .get(`/api/1/${name}`, {
      params: {
        ...params,
      },
    })
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function getGlobalSettings() {
  return axios
    .get(`/api/1/globalsettings`)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function getGlobalSettingByCode(code) {
  return axios
    .get(`/api/1/globalsettings/${code}`)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function updateGlobalSetting(data) {
  return axios
    .put(`/api/1/globalsettings/`, data)
    .then(response => {
      store.commit('notifications/ADD', {
        type: 'success',
        message: 'Global setting successfully updated',
      })
      return response.data
    })
    .catch(() => {})
}

export function getUsers(requestParams) {
  const params = requestParams
    ? {
        ...requestParams,
        size: requestParams.size,
        page: requestParams.page - 1,
        sort_property: modifySortProperty(requestParams.sort_property),
        sort_direction: requestParams.sort_direction ? 'DESC' : 'ASC',
      }
    : {
        size: 10,
        page: 0,
        sort_property: 'login',
        sort_direction: 'ASC',
      }
  return axios
    .get(`/api/1/user`, {
      params: {
        ...params,
      },
    })
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function getUserById(id) {
  return axios
    .get(`/api/1/user/${id}`)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function createUser(data) {
  return axios
    .post(`/api/1/user/`, data)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function updateUser(data) {
  return axios
    .put(`/api/1/user/`, data)
    .then(response => {
      store.commit('notifications/ADD', {
        type: 'success',
        message: 'User successfully updated',
      })
      return response.data
    })
    .catch(() => {})
}

export function deleteUser(id) {
  return axios
    .delete(`/api/1/user/${id}`)
    .then(response => {
      return response
    })
    .catch(() => {})
}

export function getUserRoles(id) {
  return axios
    .get(`/api/1/user/${id}/roles`)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function getUserRegions(id) {
  return axios
    .get(`/api/1/user/${id}/regions`)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function getUserOrganisations(id) {
  return axios
    .get(`/api/1/user/${id}/organizations`)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function getCurrentUserRegions() {
  return axios
    .get(`/api/1/user/regions`)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function getCurrentUserOrganisations() {
  return axios
    .get(`/api/1/user/organizations`)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function getRoles(requestParams) {
  const params = requestParams
    ? {
        size: requestParams.size,
        page: requestParams.page - 1,
        sort_property: modifySortProperty(requestParams.sort_property),
        sort_direction: requestParams.sort_direction ? 'DESC' : 'ASC',
      }
    : {
        size: 10,
        page: 0,
        sort_property: 'name',
        sort_direction: 'ASC',
      }
  return axios
    .get(`/api/1/role`, {
      params: {
        ...params,
      },
    })
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function getContacts(requestParams) {
  const params = requestParams
    ? {
        ...requestParams,
        size: requestParams.size,
        page: requestParams.page - 1,
        sort_property: modifySortProperty(requestParams.sort_property),
        sort_direction: requestParams.sort_direction ? 'DESC' : 'ASC',
      }
    : {
        size: 10,
        page: 0,
        sort_property: 'lastName',
        sort_direction: 'ASC',
      }
  return axios
    .get(`/api/1/contact`, {
      params: {
        ...params,
      },
    })
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function getContactById(id) {
  return axios
    .get(`/api/1/contact/${id}`)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function createContact(data) {
  return axios
    .post(`/api/1/contact/`, data)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function updateContact(data) {
  return axios
    .put(`/api/1/contact/`, data)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function deleteContact(id) {
  return axios
    .delete(`/api/1/contact/${id}`)
    .then(response => {
      return response
    })
    .catch(() => {})
}

export function getCommunications(payload) {
  return axios
    .get(`/api/1/contact/${payload.contactId}/communication`, {
      params: {
        size: payload.params.size,
        page: payload.params.page - 1,
        sort_property: modifySortProperty(payload.params.sort_property),
        sort_direction: payload.params.sort_direction ? 'DESC' : 'ASC',
      },
    })
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function createCommunication(data) {
  return axios
    .post(`/api/1/contactcommunication/`, data)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function updateCommunication(data) {
  return axios
    .put(`/api/1/contactcommunication/`, data)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function deleteCommunication(id) {
  return axios
    .delete(`/api/1/contactcommunication/${id}`)
    .then(response => {
      return response
    })
    .catch(() => {})
}

export function getGoods(requestParams) {
  if (
    !Object.keys(requestParams).includes('filter_group_comparison') &&
    !Object.keys(requestParams).includes('filter_group_value')
  ) {
    requestParams['filter_group_comparison'] = 'NE'
    requestParams['filter_group_value[]'] = 5
  }
  const params = requestParams
    ? {
        ...requestParams,
        size: requestParams.size,
        page: requestParams.page - 1,
        sort_property: modifySortProperty(requestParams.sort_property),
        sort_direction: requestParams.sort_direction ? 'DESC' : 'ASC',
      }
    : {
        size: 10,
        page: 0,
        sort_property: 'shortName',
        sort_direction: 'ASC',
      }
  return axios
    .get(`/api/1/goods`, {
      params: {
        ...params,
      },
    })
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function getGoodsById(id) {
  return axios
    .get(`/api/1/goods/${id}`)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function createGoods(data) {
  return axios
    .post(`/api/1/goods/`, data)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function updateGoods(data) {
  return axios
    .put(`/api/1/goods/`, data)
    .then(response => {
      store.commit('notifications/ADD', {
        type: 'success',
        message: `Goods "${response.data.fullName}" successfully updated`,
      })
      return response.data
    })
    .catch(() => {})
}

export function deleteGoods(id) {
  return axios
    .delete(`/api/1/goods/${id}`)
    .then(response => {
      return response
    })
    .catch(() => {})
}

export function getGoodsCharacteristic(payload) {
  return axios
    .get(`/api/1/goods/${payload.goodsId}/characteristic`, {
      params: {
        size: payload.params.size,
        page: payload.params.page - 1,
        // sort_property: null,
        // sort_direction: null,
      },
    })
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function deleteGoodsCharacteristic(id) {
  return axios
    .delete(`/api/1/goodscharacteristic/${id}`)
    .then(response => {
      return response
    })
    .catch(() => {})
}

export function createGoodsCharacteristic(data) {
  return axios
    .post(`/api/1/goodscharacteristic/`, data)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function getGoodsDrumoil(id) {
  return axios
    .get(`/api/1/goods/${id}/drumoil`)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function createGoodsDrumoil(payload) {
  return axios
    .post(`/api/1/goods/${payload.goodsId}/drumoil`, payload.data)
    .then(response => {
      return response.data
    })
}

export function getGoodsDrumvolume(id) {
  return axios
    .get(`/api/1/goods/${id}/drumvolume`)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function createGoodsDrumvolume(payload) {
  return axios
    .post(`/api/1/goods/${payload.goodsId}/drumvolume`, payload.data)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function getGoodsEquippedbarrel(id) {
  return axios
    .get(`/api/1/goods/${id}/equippedbarrel`)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function getOrganizations(requestParams) {
  const params = requestParams
    ? {
        ...requestParams,
        size: requestParams.size,
        page: requestParams.page - 1,
        sort_property: modifySortProperty(requestParams.sort_property),
        sort_direction: requestParams.sort_direction ? 'DESC' : 'ASC',
      }
    : {
        size: 10,
        page: 0,
        sort_property: 'shortName',
        sort_direction: 'ASC',
      }
  return axios
    .get(`/api/1/organization`, {
      params: {
        ...params,
      },
    })
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function createOrganization(data) {
  return axios.post(`/api/1/organization/`, data).then(response => {
    return response.data
  })
}

export function updateOrganization(data) {
  return axios.put(`/api/1/organization/`, data).then(response => {
    return response.data
  })
}

export function getItems(itemName, requestParams) {
  const params = requestParams
    ? {
        ...requestParams,
        size: requestParams.size,
        page: requestParams.page - 1,
        sort_property: modifySortProperty(requestParams.sort_property),
        sort_direction: requestParams.sort_direction ? 'DESC' : 'ASC',
      }
    : defaultRequestParams(itemName)
  return axios
    .get(`/api/1/${itemName}`, {
      params: {
        ...params,
      },
    })
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function getItemById(target, itemId, requestParams) {
  const params = requestParams
    ? {
        ...requestParams,
        size: requestParams.size,
        page: requestParams.page - 1,
        sort_property: modifySortProperty(requestParams.sort_property),
        sort_direction: requestParams.sort_direction ? 'DESC' : 'ASC',
      }
    : defaultRequestParams(target)
  return axios
    .get(`/api/1/${target}/${itemId}`, {
      params: {
        ...params,
      },
    })
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function getSubItems(itemName, itemId, subItemName, requestParams) {
  const params = requestParams
    ? {
        ...requestParams,
        size: requestParams.size,
        page: requestParams.page - 1,
        sort_property: modifySortProperty(requestParams.sort_property),
        sort_direction: requestParams.sort_direction ? 'DESC' : 'ASC',
      }
    : defaultRequestParams(itemName)
  return axios
    .get(`/api/1/${itemName}/${itemId}/${subItemName}`, {
      params: {
        ...params,
      },
    })
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function getSubItemById(item, itemId, subItem) {
  return axios
    .get(`/api/1/${item}/${itemId}/${subItem}`)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function createItem(target, item) {
  return axios
    .post(`/api/1/${target}/`, item)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function updateItem(target, item) {
  return axios
    .put(`/api/1/${target}/`, item)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function createSubItem(target, targetId, subTarget, item) {
  return axios
    .post(`/api/1/${target}/${targetId}/${subTarget}`, item)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function updateSubItem(target, targetId, subTarget, item) {
  return axios
    .put(`/api/1/${target}/${targetId}/${subTarget}`, item)
    .then(response => {
      return response.data
    })
    .catch(() => {})
}

export function deleteItem(target, id) {
  return axios
    .delete(`/api/1/${target}/${id}`)
    .then(response => {
      return response
    })
    .catch(() => {})
}

export function getQuantityGoods(orgwarehouseid) {
  return axios
    .get(`/api/1/warehouse/quantitygoods/`, {
      params: {
        orgwarehouseid,
      },
    })
    .then(response => {
      return response
    })
    .catch(() => {})
}

export function changePlug(data) {
  return axios
    .post(`/api/1/changeplug`, data)
    .then(response => {
      return response
    })
    .catch(() => {})
}

export function getGoodsBySerial(params) {
  return axios
    .get(`/api/1/warehouse/goodsbyserial`, {
      params: {
        ...params,
      },
    })
    .then(response => {
      return response
    })
    .catch(() => {})
}

export function getGoodsBySerialV1(params) {
  return axios
    .get(`/api/1/warehouse/searchfordevices`, {
      params: {
        ...params,
      },
    })
    .then(response => {
      return response
    })
    .catch(() => {})
}

function getBase64(file) {
  return new Promise(resolve => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = function() {
      const csvData = reader.result.replace(/(data:).*(base64,)/, '')
      resolve(csvData)
    }
  })
}

export function uploadGoodsCSV(goodsGroupCode, file) {
  return getBase64(file).then(response => {
    return axios
      .post(`/api/1/goods/loadCSV/`, {
        goodsGroupCode,
        csvData: response,
      })
      .then(response => {
        return response.data
      })
  })
}

export function sendVerificationLink(email) {
  return axios
    .post(`/api/1/user/forgotpassword`, { email })
    .then(response => response.data)
    .catch(() => {})
}

export function resetPassword(token, password) {
  return axios
    .post(`/api/1/user/resetpassword`, { token, password })
    .then(response => response.data)
    .catch(() => {})
}

export function validateToken(token) {
  return axios
    .get(`/api/1/user/validatetoken/${token}`)
    .then(response => response.data)
}

export function get(path, params = null) {
  return axios.get(`/api/1/${path}`, params)
}

// function errorHandler(ex) {
//   switch (ex.response.status) {
//     case 401:
//       store.dispatch('auth/logOut')
//       // router.push('/login')
//       break
//     case 404:
//       break
//     default:
//       store.commit('notifications/ADD', {
//         type: 'error',
//         message:
//           ex.response.data.message ||
//           ex.response.data.error_description ||
//           'Some server error',
//       })
//       break
//   }
// }

export function executeAll(requests, mapResultsFn) {
  return axios
    .all(requests)
    .then(
      axios.spread((...responses) => {
        return mapResultsFn(responses)
      })
    )
    .catch(() => {})
}

export function getAllEvents(requestParams) {
  var parallelRequestsNumber = 67
  let requests = []
  for (let i = 1; i <= parallelRequestsNumber; i++) {
    requests.push(
      getItems('event', {
        size: 150,
        page: i,
        sort_property: requestParams
          ? requestParams.sort_property
            ? modifySortProperty(requestParams.sort_property)
            : 'measureTime'
          : 'measureTime',
        sort_direction: requestParams
          ? requestParams.sort_direction
            ? requestParams.sort_direction
            : 'DESC'
          : 'DESC',
      })
    )
  }
  return executeAll(requests, results => {
    let events = []
    for (const result of results) {
      if (result && result.content)
        Array.prototype.push.apply(events, result.content)
    }
    return events
  }).then(result => result)
}

export function getReportData(report, requestParams) {
  const params = requestParams
    ? {
        ...requestParams,
        size: requestParams.size,
        page: requestParams.page - 1 || 0,
        sort_property: modifySortProperty(requestParams.sort_property),
        sort_direction: requestParams.sort_direction ? 'DESC' : 'ASC',
      }
    : {
        size: 10,
        page: 0,
        sort_property: 'organisation',
        sort_direction: 'ASC',
      }
  return axios
    .get(`/api/1/report/${report}`, {
      params: { ...params },
    })
    .then(response => {
      return response.data
    })
}

export function getColumnsToDisplay(report) {
  return axios
    .get(`/api/1/user_settings/columns/${report}`)
    .then(response => response.data)
}

export function updateColumnsToDisplay(report, columns) {
  return axios
    .put(`/api/1/user_settings/columns/${report}`, columns)
    .then(response => {
      return response
    })
    .catch(() => {})
}

export default {
  get,
  getDefaultOrganization,
  getOrganizationChildren,
  getOrganizationChild,
  createDocument,
  deleteDocument,
  getDocument,
  finalizeDocument,
  finalizeDocumentNoErrorHandler,
  shipNoErrorHandler,
  updateDocument,
  markDeviceAsReady,
  getSpecifications,
  getSerialNumbers,
  createSpecification,
  deleteSpecification,
  updateSpecification,
  getDictionary,
  getGlobalSettings,
  getGlobalSettingByCode,
  updateGlobalSetting,
  getUsers,
  getUserById,
  createUser,
  updateUser,
  deleteUser,
  getUserRoles,
  getUserRegions,
  getUserOrganisations,
  getRoles,
  getContacts,
  getContactById,
  deleteContact,
  createContact,
  updateContact,
  getCommunications,
  createCommunication,
  updateCommunication,
  deleteCommunication,
  getGoods,
  getGoodsById,
  createGoods,
  updateGoods,
  deleteGoods,
  getGoodsCharacteristic,
  getGoodsDrumoil,
  getGoodsDrumvolume,
  deleteGoodsCharacteristic,
  createGoodsCharacteristic,
  createGoodsDrumoil,
  getGoodsEquippedbarrel,
  createGoodsDrumvolume,
  getOrganizations,
  createOrganization,
  updateOrganization,
  getItems,
  getItemById,
  getSubItems,
  getSubItemById,
  createItem,
  updateItem,
  deleteItem,
  createSubItem,
  updateSubItem,
  getQuantityGoods,
  changePlug,
  getGoodsBySerial,
  getGoodsBySerialV1,
  uploadGoodsCSV,
  getPermissions,
  getReportData,
  getColumnsToDisplay,
  updateColumnsToDisplay,
  executeAll,
  getAllEvents,
  getCurrentUserRegions,
  getCurrentUserOrganisations,
  sendVerificationLink,
  resetPassword,
  validateToken,
  handleError,
}
